import {
  Button,
  ButtonList,
  DropdownButton,
  FlexBox,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import uniq from 'lodash/uniq';
import { useCallback, useMemo } from 'react';

import { conversationsApi } from 'apis/conversations';
import { BulkActionNotificationParams } from 'apis/notifications';
import { appActions } from 'app/app.actions';
import {
  BulkModalActionType,
  conversationsBulkModalActions,
  transformBulkActionRequestInputToAction,
} from 'app/components/appModals/components/conversationsBulkModal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type NotificationBulkActionButtonsType = 'all' | 'failed' | 'succeeded';

type NotificationBulkActionButtonsProps = {
  bulkActionData: BulkActionNotificationParams;
};

export const NotificationBulkActionButtons = ({
  bulkActionData,
}: NotificationBulkActionButtonsProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const { bulk_id: bulkId, notification_type: bulkStatus } = bulkActionData;

  const getAllConversationIds = useCallback(async (): Promise<number[]> => {
    if (!bulkId) return [];

    try {
      const response = await dispatch(
        conversationsApi.endpoints.readConversationBulkAction.initiate({
          bulkId,
        }),
      );

      return isResponseWithData(response)
        ? uniq(response.data.data.conversation_ids)
        : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [bulkId, dispatch]);

  const getFailedConversationIds = useCallback(async (): Promise<number[]> => {
    if (!bulkId) return [];

    try {
      const response = await dispatch(
        conversationsApi.endpoints.readConversationBulkActionFailedActions.initiate(
          {
            bulkId,
          },
        ),
      );

      return isResponseWithData(response)
        ? uniq(
            response.data.data.failed_actions.map(
              (action) => action.conversation_id,
            ),
          )
        : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [bulkId, dispatch]);

  const getSucceededConversationIds = useCallback(async (): Promise<
    number[]
  > => {
    try {
      const [allIDs, failedIDs] = await Promise.all([
        getAllConversationIds(),
        getFailedConversationIds(),
      ]);

      const successIDs = allIDs.filter((id) => failedIDs.indexOf(id) === -1);
      return uniq(successIDs);
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [getAllConversationIds, getFailedConversationIds]);

  const getConversationIdsGetterFn = useCallback(
    (type: NotificationBulkActionButtonsType): (() => Promise<number[]>) => {
      switch (type) {
        case 'all':
          return getAllConversationIds;
        case 'succeeded':
          return getSucceededConversationIds;
        case 'failed':
        default:
          return getFailedConversationIds;
      }
    },
    [
      getAllConversationIds,
      getFailedConversationIds,
      getSucceededConversationIds,
    ],
  );

  /**
   * Handles performing a new bulk action on the same conversations from the
   * existing bulk action.
   */
  const handlePerformNewBulkAction = useCallback(
    async (type: NotificationBulkActionButtonsType): Promise<void> => {
      const getter = getConversationIdsGetterFn(type);

      try {
        const conversationIds = await getter();

        dispatch(
          appActions.showModal({
            modal: {
              type: 'app:conversationsBulk',
              arg: {
                conversationIds,
                location: 'notifications',
              },
            },
          }),
        );
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch, getConversationIdsGetterFn],
  );

  /** Handles retrying a failed bulk action with the same parameters. */
  const handleRetryBulkAction = useCallback(async (): Promise<void> => {
    if (!bulkId) return;

    try {
      const failedConversationIds = await getFailedConversationIds();
      if (!failedConversationIds.length) return;

      const bulkActionResponse = await dispatch(
        conversationsApi.endpoints.readConversationBulkAction.initiate({
          bulkId,
        }),
      );

      if (isResponseWithData(bulkActionResponse)) {
        const { action_type: type, request_input } =
          bulkActionResponse.data.data;
        const requestInput = JSON.parse(request_input);

        const action = transformBulkActionRequestInputToAction(
          type as BulkModalActionType,
          failedConversationIds,
          requestInput,
        );

        if (action) {
          dispatch(
            conversationsBulkModalActions.initBulkUpdate({
              actions: [action],
            }),
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [bulkId, dispatch, getFailedConversationIds]);

  const dropdownButtons = useMemo(
    () => (
      <>
        {bulkStatus !== 'BulkActionFailed' && (
          <ButtonList.Item onClick={() => handlePerformNewBulkAction('all')}>
            Update all conversations
          </ButtonList.Item>
        )}

        {(bulkStatus === 'BulkActionFailed' ||
          bulkStatus === 'BulkActionPartialFailed') && (
          <ButtonList.Item onClick={() => handlePerformNewBulkAction('failed')}>
            Update failed conversations
          </ButtonList.Item>
        )}

        {bulkStatus === 'BulkActionPartialFailed' && (
          <ButtonList.Item
            onClick={() => handlePerformNewBulkAction('succeeded')}
          >
            Update succeeded conversations
          </ButtonList.Item>
        )}
      </>
    ),
    [bulkStatus, handlePerformNewBulkAction],
  );

  return (
    <FlexBox alignItems="center" gap="200">
      {(bulkStatus === 'BulkActionFailed' ||
        bulkStatus === 'BulkActionPartialFailed') && (
        <Button variant="secondary" onClick={handleRetryBulkAction}>
          Retry
        </Button>
      )}

      <DropdownButton dropdown={<ButtonList>{dropdownButtons}</ButtonList>}>
        Add action
      </DropdownButton>
    </FlexBox>
  );
};
